import React, { useState, useEffect, useRef } from 'react';
import { 
  PenTool,
  Upload,
  Download,
  Scan,
  Save,
  CheckCircle,
  XCircle
} from 'lucide-react';
import purchaseRequestService from '@/services/purchaseRequestService';
import SignatureCanvas from 'react-signature-canvas';

const GMDSignature = () => {
  const [signatureMethod, setSignatureMethod] = useState('upload');
  const [signatureFile, setSignatureFile] = useState(null);
  const [signaturePreview, setSignaturePreview] = useState('/signature.png');
  const [uploadingSignature, setUploadingSignature] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [settings, setSettings] = useState({});
  const [signatureEnabled, setSignatureEnabled] = useState(true);
  const signatureCanvasRef = useRef(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  // Always show the local signature
  useEffect(() => {
    setSignaturePreview('/signature.png');
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await purchaseRequestService.getSettings();
      if (response.data.success) {
        setSettings(response.data.data?.settings || {});
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const handleSignatureUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        setError('Signature file must be less than 2MB');
        return;
      }
      
      if (!['image/png', 'image/jpeg', 'image/jpg'].includes(file.type)) {
        setError('Only PNG and JPEG images are allowed');
        return;
      }
      
      setSignatureFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setSignaturePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDrawnSignature = () => {
    if (signatureCanvasRef.current && !signatureCanvasRef.current.isEmpty()) {
      const dataUrl = signatureCanvasRef.current.toDataURL('image/png');
      setSignaturePreview(dataUrl);
      
      fetch(dataUrl)
        .then(res => res.blob())
        .then(blob => {
          const file = new File([blob], 'signature.png', { type: 'image/png' });
          setSignatureFile(file);
        });
    } else {
      setError('Please draw a signature first');
    }
  };

  const clearSignature = () => {
    if (signatureCanvasRef.current) {
      signatureCanvasRef.current.clear();
    }
    setSignatureFile(null);
    setSignaturePreview(null);
  };

  const uploadSignature = async () => {
    if (!signatureFile) {
      setError('Please provide a signature first');
      return;
    }

    try {
      setUploadingSignature(true);
      setError(null);

      const formData = new FormData();
      formData.append('signature', signatureFile);

      const response = await purchaseRequestService.uploadGMDSignature(formData);

      if (response.data.success) {
        setSuccess('GMD signature uploaded successfully!');
        setTimeout(() => setSuccess(null), 3000);
        
        setSettings(prev => ({
          ...prev,
          gmd_signature_url: response.data.data.signature_url
        }));
        
        setSignaturePreview(response.data.data.signature_url);
      } else {
        throw new Error(response.data.message || 'Failed to upload signature');
      }
    } catch (error) {
      console.error('Error uploading signature:', error);
      setError(error.response?.data?.message || error.message || 'Failed to upload signature');
    } finally {
      setUploadingSignature(false);
    }
  };

  const downloadSignature = () => {
    if (signaturePreview) {
      const link = document.createElement('a');
      link.href = signaturePreview;
      link.download = 'gmd-signature.png';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      setSuccess('Signature downloaded successfully!');
      setTimeout(() => setSuccess(null), 3000);
    }
  };

  const handleScannerCapture = async () => {
    try {
      if (window.SigWebTablet) {
        const tablet = new window.SigWebTablet();
        tablet.SetDisplayXSize(500);
        tablet.SetDisplayYSize(200);
        tablet.SetTabletState(0, tablet.tmr);
        
        const result = await tablet.GetSigImageB64();
        if (result) {
          const dataUrl = `data:image/png;base64,${result}`;
          setSignaturePreview(dataUrl);
          
          fetch(dataUrl)
            .then(res => res.blob())
            .then(blob => {
              const file = new File([blob], 'signature.png', { type: 'image/png' });
              setSignatureFile(file);
            });
        }
      } else {
        setError('Topaz scanner not detected. Please install Topaz SigWeb software.');
      }
    } catch (error) {
      console.error('Scanner error:', error);
      setError('Failed to capture signature from scanner. Please ensure the scanner is connected.');
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">GMD Signature</h1>
        <p className="text-gray-600 mt-1">Manage your digital signature for purchase request approvals</p>
      </div>

      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center">
            <CheckCircle className="h-5 w-5 text-green-400 mr-2" />
            <span className="text-green-800">{success}</span>
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <XCircle className="h-5 w-5 text-red-400 mr-2" />
            <span className="text-red-800">{error}</span>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <PenTool className="h-5 w-5 text-blue-600 mr-2" />
            <h3 className="text-lg font-semibold text-gray-900">Digital Signature</h3>
          </div>
          <label className="flex items-center cursor-pointer">
            <span className="mr-3 text-sm font-medium text-gray-700">
              {signatureEnabled ? 'Enabled' : 'Disabled'}
            </span>
            <div className="relative">
              <input
                type="checkbox"
                checked={signatureEnabled}
                onChange={(e) => setSignatureEnabled(e.target.checked)}
                className="sr-only"
              />
              <div className={`block w-14 h-8 rounded-full transition-colors ${
                signatureEnabled ? 'bg-green-600' : 'bg-gray-300'
              }`}></div>
              <div className={`absolute left-1 top-1 bg-white w-6 h-6 rounded-full transition-transform ${
                signatureEnabled ? 'transform translate-x-6' : ''
              }`}></div>
            </div>
          </label>
        </div>
        
        <p className="text-sm text-gray-600 mb-6">
          Upload or capture your signature for purchase request documents. You can download it anytime.
        </p>

        {/* Current Signature Display */}
        <div className="mb-6 border-2 border-gray-200 rounded-lg p-4 bg-gray-50">
          <p className="text-sm font-medium text-gray-700 mb-3">Current GMD Signature:</p>
          <div className="bg-white p-4 rounded border border-gray-200 flex items-center justify-center">
            <img
              src={signaturePreview}
              alt="GMD Signature"
              className="max-h-32 object-contain"
            />
          </div>
        </div>
        
        <div className="flex space-x-4 mb-6">
          <button
            onClick={() => setSignatureMethod('upload')}
            className={`flex-1 py-3 px-4 rounded-lg border-2 transition-all ${
              signatureMethod === 'upload'
                ? 'border-blue-600 bg-blue-50 text-blue-700'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <Upload className="h-5 w-5 mx-auto mb-1" />
            <span className="text-sm font-medium">Upload Image</span>
          </button>
          
          <button
            onClick={() => setSignatureMethod('draw')}
            className={`flex-1 py-3 px-4 rounded-lg border-2 transition-all ${
              signatureMethod === 'draw'
                ? 'border-blue-600 bg-blue-50 text-blue-700'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <PenTool className="h-5 w-5 mx-auto mb-1" />
            <span className="text-sm font-medium">Draw Signature</span>
          </button>
          
          <button
            onClick={() => setSignatureMethod('scanner')}
            className={`flex-1 py-3 px-4 rounded-lg border-2 transition-all ${
              signatureMethod === 'scanner'
                ? 'border-blue-600 bg-blue-50 text-blue-700'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <Scan className="h-5 w-5 mx-auto mb-1" />
            <span className="text-sm font-medium">Topaz Scanner</span>
          </button>
        </div>
        
        {signatureMethod === 'upload' && (
          <div className="space-y-4">
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-400 transition-colors">
              <input
                type="file"
                accept="image/png,image/jpeg,image/jpg"
                onChange={handleSignatureUpload}
                className="hidden"
                id="signature-upload"
              />
              <label htmlFor="signature-upload" className="cursor-pointer">
                <Upload className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                <p className="text-sm font-medium text-gray-700 mb-1">
                  Click to upload signature image
                </p>
                <p className="text-xs text-gray-500">PNG or JPEG, max 2MB</p>
              </label>
            </div>
          </div>
        )}
        
        {signatureMethod === 'draw' && (
          <div className="space-y-4">
            <div className="border-2 border-gray-300 rounded-lg overflow-hidden">
              <SignatureCanvas
                ref={signatureCanvasRef}
                canvasProps={{
                  className: 'w-full h-48 bg-white',
                  style: { touchAction: 'none' }
                }}
                backgroundColor="white"
              />
            </div>
            <div className="flex space-x-3">
              <button
                onClick={() => signatureCanvasRef.current?.clear()}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Clear
              </button>
              <button
                onClick={handleDrawnSignature}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Save Signature
              </button>
            </div>
          </div>
        )}
        
        {signatureMethod === 'scanner' && (
          <div className="space-y-4">
            <div className="border-2 border-gray-300 rounded-lg p-8 text-center">
              <Scan className="h-12 w-12 text-gray-400 mx-auto mb-3" />
              <p className="text-sm font-medium text-gray-700 mb-1">
                Topaz Signature Scanner
              </p>
              <p className="text-xs text-gray-500 mb-4">
                Ensure your Topaz scanner is connected and SigWeb software is installed
              </p>
              <button
                onClick={handleScannerCapture}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Capture Signature
              </button>
            </div>
          </div>
        )}
        
        {signatureFile && (
          <div className="mt-6 space-y-4">
            <div className="flex space-x-3">
              {signatureFile && (
                <button
                  onClick={uploadSignature}
                  disabled={uploadingSignature}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {uploadingSignature ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Upload Signature
                    </>
                  )}
                </button>
              )}
              
              <button
                onClick={downloadSignature}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors flex items-center"
              >
                <Download className="h-4 w-4 mr-2" />
                Download
              </button>
              
              <button
                onClick={clearSignature}
                className="px-4 py-2 border border-red-300 rounded-lg text-red-700 hover:bg-red-50 transition-colors"
              >
                Remove
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GMDSignature;
